import React, { useEffect, useState, useRef } from 'react';
import { CheckCheck, ChevronLeft, Clock, FileQuestion, ListChecks, MessageCircleMore, ScrollTextIcon, User } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePopUpContext } from '../contexts/Popup';
import { useLoaderContext } from '../contexts/loader';
import { useUserContext } from '../contexts/user';
import { db } from '../Database';
import { useCheckForNewToken } from '../checkForNewToken';

const QuizInfoPage = () => {
  const navigate = useNavigate();

  const { id } = useParams();

  const [quizData, setQuizData] = useState({});
  const [showEnterPassword, setShowEnterPassword] = useState(false);
  const [passwordMatches, setPasswordMatches] = useState(false);

  const { setMessageContent } = usePopUpContext();
  const { setIsLoading } = useLoaderContext();
  const { userInfo, setUserInfo, userJWT, setUserJWT, setUserIndex, setUser, appLanguage } = useUserContext();
  const { checkForNewToken } = useCheckForNewToken();

  useEffect(() => {
    setIsLoading(prev => prev + 1);
    fetch("https://quiz-battle-api.vercel.app/api/quiz/" + id, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${userJWT}`,
      },
    })
      .then(res => res.json())
      .then(data => {
        setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
        checkForNewToken(data, setUserJWT, setUserInfo, setUser, setUserIndex, navigate, setMessageContent);
        if (data.failed || data.ok === false) {
          setMessageContent({
            title: appLanguage === "ar" ? "فشل الجلب" : "Failed To Fetch",
            message: appLanguage === "ar" ? "فشل في جلب الاختبار من قاعدة البيانات!" : "Failed to fetch the quiz from the database!",
          })
          navigate("/home");
        } else {
          setQuizData(data);
        }
      }).catch(err => {
        setIsLoading(prev => prev !== 0 ? prev - 1 : prev);

        navigate("/home");
        setMessageContent({
          title: appLanguage === "ar" ? "فشل الجلب" : "Failed To Fetch",
          message: appLanguage === "ar" ? "فشل في جلب الاختبار من قاعدة البيانات!" : "Failed to fetch the quiz from the database!",
        })
      })
  }, [])

  useEffect(() => {
    if (userInfo.role === "teacher") {
      navigate("/home");
      setMessageContent({
        title: appLanguage === "ar" ? "الوصول مرفوض" : "Access Denied",
        message: appLanguage === "ar" ? "يمكن للطلاب فقط الوصول إلى الاختبارات." : "Only the students can access the quizzes.",
      })
    }
  }, [userInfo])

  const startQuiz = () => {
    if(quizData.password) {
      setShowEnterPassword(true);
    }else {
      setIsLoading(prev => prev + 1);
      fetch("https://quiz-battle-api.vercel.app/api/start-quiz" , {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${userJWT}`,
        }, 
        body: JSON.stringify({
          password: null,
          quizID: id,
        }),
      }).then(res => res.json())
      .then(data => {
        checkForNewToken(data, setUserJWT, setUserInfo, setUser, setUserIndex, navigate, setMessageContent);
          if (data.failed) {
            setMessageContent({
              title: appLanguage === "ar" ? "فشل البدء" : "Starting Failed",
              message: appLanguage === "ar" ? "حدث خطأ أثناء بدء الإختبار." : "Oops! something went wrong while starting the quiz.",
            })
          } else {
            setUserInfo(prev => ({...prev , playingQuiz: id, quizEndsAt: data.quiz_ends_at,}))
            navigate("/quiz/" + id);
          }
          setIsLoading((prev) => prev !== 0 ? prev - 1 : 0);
        }).catch((err) => {
          setIsLoading((prev) => prev !== 0 ? prev - 1 : 0);
          setMessageContent({
            title: appLanguage === "ar" ? "فشل البدء" : "Starting Failed",
            message: appLanguage === "ar" ? "حدث خطأ أثناء بدء الإختبار." : "Oops! something went wrong while starting the quiz.",
          })
        })
    }
  }

  return (
    <div className="quiz_info_container">
      <header>
        <ChevronLeft onClick={() => navigate("/home")} />
        <p>{appLanguage === "ar" ? "بدء الاختبار" : "Start Quiz"}</p>
      </header>
      <div className="content_container">
        <div className="page_content">
          <p className="title">{quizData.category}</p>
          <div className="data">
            <div className="item teacher">
              <div className="icon"><User /></div>
              <div className="text">
                <p className="key">{appLanguage === "ar" ? "اسم المدرس" : "Teacher Name"}</p>
                <p className="value">{quizData.from_name}</p>
              </div>
            </div>
            <div className="item">
              <div className="icon"><FileQuestion /></div>
              <div className="text">
                <p className="key">{appLanguage === "ar" ? "عدد الأسئلة" : "Questions Number"}</p>
                <p className="value">{quizData.questions ? quizData.questions.length : 0}</p>
              </div>
            </div>
            <div className="item">
              <div className="icon"><Clock /></div>
              <div className="text">
                <p className="key">{appLanguage === "ar" ? "وقت الاختبار" : "Quiz Time"}</p>
                <p className="value">{quizData.questions ? (`${Math.floor(quizData.questions.length * 30 / 60) < 10 ? "0" + Math.floor(quizData.questions.length * 30 / 60) : Math.floor(quizData.questions.length * 30 / 60)}:${quizData.questions.length * 30 % 60 < 10 ? "0" + quizData.questions.length * 30 % 60 : quizData.questions.length * 30 % 60}`) : "00:00"}</p>
              </div>
            </div>
            <div className="item">
              <div className="icon"><CheckCheck /></div>
              <div className="text">
                <p className="key">{appLanguage === "ar" ? "إجمالي النقاط" : "Total Points"}</p>
                <p className="value">{quizData.questions ? quizData.questions.length * 5 : 0}</p>
              </div>
            </div>
          </div>
          <div className="description">
            <p className="title description"><ScrollTextIcon size={18} /> {appLanguage === "ar" ? "الوصف" : "Description"}</p>
            <p className="value">{quizData.description ? quizData.description : (appLanguage === "ar" ? "لا يوجد وصف" : "No Description Provided")}</p>
          </div>
        </div>
      </div>
      {userInfo.playedQuizzes && userInfo.playedQuizzes.includes(id) ? <button id="chat" className="show_result" onClick={() => navigate("/chat/" + id)}>{appLanguage === "ar" ? "الدردشة" : "Chat"} <MessageCircleMore /></button> : undefined}
      <button id="start_quiz" className={userInfo.playedQuizzes ? (userInfo.playedQuizzes.includes(id) ? "show_result" : "start_quiz") : "start_quiz"} onClick={() => {userInfo.playedQuizzes && userInfo.playedQuizzes.includes(id) ? navigate("/result/" + id) : startQuiz()}}>{userInfo.playedQuizzes ? (userInfo.playedQuizzes.includes(id) ? <>{appLanguage === "ar" ? "عرض النتيجة" : "Show Result"} <ListChecks /></> : (appLanguage === "ar" ? "بدء الاختبار" : "Start Quiz")) : (appLanguage === "ar" ? "بدء الاختبار" : "Start Quiz")}</button>
      {showEnterPassword ? <EnterPassword setPasswordMatches={setPasswordMatches} setShowEnterPassword={setShowEnterPassword} setMessageContent={setMessageContent} setIsLoading={setIsLoading} userJWT={userJWT} appLanguage={appLanguage} /> : undefined}
    </div>
  )
}

const EnterPassword = ({ setShowEnterPassword, setMessageContent, setIsLoading, userJWT, appLanguage }) => {
  const { id } = useParams();

  const navigate = useNavigate();
  const passwordInputRef = useRef();

  const { setUserJWT, setUserInfo, setUserIndex, setUser } = useUserContext();
  const { checkForNewToken } = useCheckForNewToken();

  const passwordMatches = () => {
    setIsLoading((prev) => prev + 1);
    if (passwordInputRef.current.value) {
      fetch("https://quiz-battle-api.vercel.app/api/start-quiz", {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${userJWT}`,
        },
        body: JSON.stringify({
          quizID: id,
          password: passwordInputRef.current.value,
        }),
      }).then(res => res.json())
        .then(data => {
          checkForNewToken(data, setUserJWT, setUserInfo, setUser, setUserIndex, navigate, setMessageContent);
          if (data.failed) {
            if(Object.keys(data).includes("passwordMatches") && !data.passwordMatches) {
              setMessageContent({
                title: appLanguage === "ar" ? "كلمة مرور خاطئة" : "Wrong Password",
                message: appLanguage === "ar" ? `كلمة المرور '${passwordInputRef.current.value}' خاطئة.` : `The password '${passwordInputRef.current.value}' is wrong.`,
              })
            }else {
              if(!Object.keys(data).includes("passwordMatches")) {
                setMessageContent({
                  title: appLanguage === "ar" ? "فشل التحقق" : "Checking Failed",
                  message: data.msg,
                })
              }
            }
          } else {
            setUserInfo(prev => ({...prev , playingQuiz: id, quizEndsAt: data.quiz_ends_at,}))
            navigate("/quiz/" + id);
          }
          setIsLoading((prev) => prev !== 0 ? prev - 1 : 0);
        }).catch((err) => {
          setIsLoading((prev) => prev !== 0 ? prev - 1 : 0);
          setMessageContent({
            title: appLanguage === "ar" ? "فشل البدء" : "Starting Failed",
            message: appLanguage === "ar" ? "حدث خطأ أثناء بدء الإختبار." : "Oops! something went wrong while starting the quiz.",
          })
        })
    } else {
      setIsLoading((prev) => prev !== 0 ? prev - 1 : 0);
      setMessageContent({
        title: appLanguage === "ar" ? "إدخال فارغ" : "Empty Input",
        message: appLanguage === "ar" ? "أدخل كلمة مرور، لا تترك الحقل فارغًا." : "Enter a password don't leave the input empty.",
      })
    }
  }

  return (
    <div className="enter_password_container">
      <div className="content">
        <p className="title">{appLanguage === "ar" ? "أدخل كلمة المرور" : "Enter Password"}</p>
        <div className="main_content">
          <input ref={passwordInputRef} type="text" placeholder={appLanguage === "ar" ? "كلمة المرور..." : "Password..."} />
          <button className="start" onClick={passwordMatches}>{appLanguage === "ar" ? "بدء الاختبار" : "Start Quiz"}</button>
          <button className="cancel" onClick={() => setShowEnterPassword(false)}>{appLanguage === "ar" ? "إلغاء" : "Cancel"}</button>
        </div>
      </div>
    </div>
  );
}

export default QuizInfoPage;